import { ModelPageConfig } from '../types';

const config: ModelPageConfig = {
  slug: 'seedance-2-0',
  modelName: 'Seedance 2.0',
  hero: {
    title: 'Seedance 2.0 AI Video Generator',
    description:
      'Seedance 2.0 introduces one of the most advanced multimodal reference systems in the industry, now available on FalcoCut. Set style with images, define movement with video, sync rhythm with audio, and shape narrative with text to achieve unprecedented control.',
    videos: [
      'https://assets.falcocut.ai/models/seedance/hip-hop-fashion-denim-jacket.mp4',
    ],
  },
  features: {
    sectionTitle: 'Why Creators Choose Seedance 2.0',
    items: [
      {
        title: 'Quad-Modal Input',
        description:
          'Seedance 2.0 utilizes a sophisticated quad-modal reference system that allows you to feed the model up to 12 distinct image, video, and audio assets simultaneously. This multi-layered input ensures that your specific character designs and art styles remain perfectly consistent across every frame of the generation.',
      },
      {
        title: 'Native Audio-Video Sync',
        description:
          'Generate high-fidelity soundscapes and visual motion in a single, synchronized pass, engineered with a native audio-video engine. Seedance 2.0 eliminates the need for manual post-production by delivering perfect lip-syncing and immersive environmental audio that matches the on-screen action exactly.',
      },
      {
        title: 'Multi-Shot Storytelling',
        description:
          'Seedance 2.0 acts as an intelligent AI director by automatically breaking down a single prompt into a professional cinematic sequence of wide, medium, and close-up shots. The advanced narrative logic maintains character stability and environmental flow, allowing you to create complex, multi-scene brand stories with zero manual cutting.',
      },
    ],
  },
  showcase: {
    title: 'See What Seedance 2.0 Can Create',
    description:
      'Use Seedance 2.0 to combine image, video, audio, and text references and generate coherent, controllable, and visually rich footage and ad creatives that feels directed—not randomly generated.',
    videoUrl:
      'https://assets.falcocut.ai/models/seedance/seedance-2-0-spiral-staircase-transition.mp4',
  },
  howTo: {
    title: 'How to Create Videos with Seedance 2.0 on FalcoCut',
    steps: [
      {
        title: 'Step 1: Describe Your Idea',
        description:
          'Tell your concept or upload a reference image so Seedance 2.0 can understand your visual direction and creative intent clearly.',
        imageUrl: 'https://assets.falcocut.ai/models/seedance/describe-your-idea.png',
      },
      {
        title: 'Step 2: Choose Seedance 2.0 Model',
        description:
          'Select the Seedance 2.0 model to unlock multimodal controls that let you guide style, motion, rhythm, and narrative with precision.',
        imageUrl:
          'https://falcocut-sgp-1375136936.cos.ap-singapore.myqcloud.com/20017-1/asset/20260423/seedance20_1830268434840850432.png',
      },
      {
        title: 'Step 3: Generate and Save',
        description:
          'Let FalcoCut process your references into a cinematic AI video, then review, refine, and save your final creation instantly.',
        imageUrl: 'https://assets.falcocut.ai/models/seedance/generate-and-save.png',
      },
    ],
  },
  whyChoose: {
    title: 'Why Choose Seedance 2.0 AI Video Generator on FalcoCut',
    description:
      'FalcoCut brings the elite Seedance 2.0 engine to your creative workflow, offering a specialized workspace for professional video generation. Use its multimodal power to craft high-impact marketing assets with unprecedented precision.',
    items: [
      {
        tag: 'Built for Professional Directors',
        heading: 'Craft Cinematic Shots\nwith Unmatched Control',
        description:
          "Master every aspect of your production by fine-tuning the specific look, artistic style, and complex motion of your video assets. Seedance 2.0 provides the granular controls needed to ensure the output aligns perfectly with your brand's creative vision.",
        bullets: [
          'Direct camera movements and character actions with high precision',
          'Ensure brand-consistent aesthetics across every frame produced',
        ],
        imageUrl:
          'https://assets.falcocut.ai/models/seedance/craft-cinematic-shots-with-unmatched-control.png',
      },
      {
        tag: 'Built for Multi-Platform Campaigns',
        heading: 'Sync Multimodal\nBrand Assets',
        description:
          'Utilize the revolutionary @-tag reference system to seamlessly blend text, images, audio, and video references into a single unified prompt. This flexible input method allows Seedance 2.0 to understand the exact context of your project by referencing your existing brand files.',
        bullets: [
          'Upload up to 12 distinct reference assets for a single video',
          'Maintain exact character and style matching using image prompts',
        ],
        imageUrl:
          'https://assets.falcocut.ai/models/seedance/sync-multimodal-brand-assets.png',
      },
      {
        tag: 'Built for Brand Storytelling',
        heading: 'Create Long Stories\nwith Perfect Consistency',
        description:
          'Produce longer sequences that maintain stable product details, faces, and environments even as the camera moves through complex scenes. Seedance 2.0 ensures that your story remains coherent from the first second to the last, eliminating the "morphing" common in basic AI tools.',
        bullets: [
          'Keep characters and objects visually identical across multiple shots',
          'Generate well‑blended transitions for professional-grade storytelling',
        ],
        imageUrl:
          'https://assets.falcocut.ai/models/seedance/create-long-stories-with-perfect-consistency.png',
      },
      {
        tag: 'Built for Performance Marketers',
        heading: 'Export Pro Videos\nfor Any Channel',
        description:
          'Rapidly deploy your high-performing creative across every major social channel with native 2K resolution and flexible aspect ratio support. Whether you need an engaging 16:9 YouTube ad or a snappy 9:16 TikTok promo, Seedance 2.0 aligns your content to platform best practices.',
        bullets: [
          'Switch between formats instantly to fit all social media requirements',
          'Deliver crystal-clear 2K visuals that stop the scroll and drive clicks',
        ],
        imageUrl:
          'https://assets.falcocut.ai/models/seedance/export-pro-videos-for-any-channel.png',
      },
    ],
  },
  whoCanUse: {
    title: 'Who Can Use Seedance 2.0 in FalcoCut',
    items: [
      {
        title: 'E-Commerce & Marketing Teams',
        description:
          'Produce high-converting product videos with existing brand assets. Generate professional-grade social media promos and captivating ads that maintain strict brand consistency without a physical photoshoot.',
        imageUrl:
          'https://assets.falcocut.ai/models/seedance/e-commerce-marketing-teams.png',
      },
      {
        title: 'Filmmakers & Storytellers',
        description:
          'Construct complex cinematic sequences and maintain stable characters and environments across different camera angles. Turn scripts into high-fidelity visual narratives in a fraction of the traditional time.',
        imageUrl:
          'https://assets.falcocut.ai/models/seedance/filmmakers-storytellers.png',
      },
      {
        title: 'Advertising Agencies',
        description:
          'Accelerate the creative pipeline by transforming raw storyboards into cinematic video proofs for rapid client approval. Iterate on high-concept ideas and deliver production-ready videos that meet demanding brand standards.',
        imageUrl: 'https://assets.falcocut.ai/models/seedance/advertising-agencies.png',
      },
    ],
  },
  videoGallery: {
    title: 'Seedance 2.0 Advanced Model Showcase',
    description:
      'Explore creative videos generated with the Seedance 2.0 model. From playful animations to cinematic storytelling, these examples show how simple prompts can turn ideas into visually rich AI videos.',
    items: [
      {
        title: 'Paws & Podcasts: Behind the Mic',
        description:
          'What happens when a polar bear, a little girl, and a hamster host a talk show? Pure, unscripted cuteness!',
        videoUrl:
          'https://assets.falcocut.ai/models/seedance/animated-kids-bear-hamster-podcast.mp4',
        prompt:
          'In a cozy studio, the "Truth Talk Radio" hosts are recording. The Big Bear and Hamster solemnly swear 100% loyalty to their company, but the Little Girl quickly roasts them by asking why they update their resumes more than she updates her social media. After a frantic, panicked moment of trying to shush her, the trio breaks into guilty, hearty laughter.',
      },
      {
        title: 'The Giant Carrot: A Forest Team-up',
        description:
          'A rabbit and a raccoon join forces for the harvest of a lifetime. Teamwork makes the dream work!',
        videoUrl:
          'https://assets.falcocut.ai/models/seedance/rabbit-raccoon-pulling-giant-carrot.mp4',
        prompt:
          'In a sunny, fenced garden, a fluffy white rabbit struggles to pull a giant carrot from the ground. A friendly raccoon soon joins in, and together they tug with all their might. With one final, powerful pull, the carrot pops out, sending both animals tumbling backward. They sit by the empty hole, look at each other, and burst into joyful laughter, celebrating their successful teamwork.',
      },
      {
        title: 'Traditional Chinese Opera Dance',
        description:
          'Experience the ethereal beauty of "Water Sleeves," a classic Chinese opera technique where movement flows like liquid silk.',
        videoUrl:
          'https://assets.falcocut.ai/models/seedance/chinese-opera-performance-water-sleeves.mp4',
        prompt:
          'The video opens with a stunning close-up of a performer\'s exquisite makeup and jeweled headdress. Under a dramatic spotlight, she performs a mesmerizing dance, masterfully swirling her long "water sleeves" (Shuixiu). Her movements are a perfect harmony of fluidity and grace, with the white silk flowing like waves through the air.',
      },
      {
        title: 'Tang Dynasty: A Spring Romance',
        description:
          'A poetic encounter during a spring outing in the Tang Dynasty. Witness the elegance of ancient lovers amidst falling blossoms.',
        videoUrl:
          'https://assets.falcocut.ai/models/seedance/tang-dynasty-love-beneath-the-blossoms.mp4',
        prompt:
          'In a stunning classical Chinese landscape, a group of Tang Dynasty nobles rides through a valley filled with blooming blossoms. The atmosphere is serene and elegant, styled like a traditional painting. A young man in a red robe dismounts to pick a delicate pink flower and presents it to a graceful lady as a romantic gesture.',
      },
      {
        title: 'Ancient Painting Reimagined: Tang Dynasty',
        description:
          'Step into history as the famous "Ladies on Horseback" come to life, capturing the leisurely elegance of a spring outing in old China.',
        videoUrl:
          'https://assets.falcocut.ai/models/seedance/tang-dynasty-ladies-horseback-spring-outing.mp4',
        prompt:
          'In a traditional Chinese painting style, a grand procession of Tang Dynasty nobles and ladies rides through a scenic countryside. The atmosphere is serene, with willows swaying and blossoms drifting in the air. The ladies, dressed in elegant, colorful gowns, converse gracefully with the gentlemen as their horses move in a rhythmic pace. This beautiful scene perfectly captures the leisurely elegance and cultural charm of an elite spring outing in ancient China.',
      },
      {
        title: "A Sip of Art: The Milkmaid's Secret",
        description:
          'Vermeer\'s "Girl with a Pearl Earring" takes a break from the frame for a refreshing glass of milk.',
        videoUrl:
          'https://assets.falcocut.ai/models/seedance/girl-with-a-pearl-earring-milk.mp4',
        prompt:
          'In a sunlit room, Johannes Vermeer\'s "Girl with a Pearl Earring" hangs on the wall next to a carton of Yuanqi Milk. In a surprising twist, the girl comes to life, reaching out of the frame to grab a glass of milk. She takes a satisfying sip with a blissful expression, but suddenly hears footsteps. She quickly retreats back into her static painted pose just as a man walks by and picks up the glass. The video concludes with a close-up of the milk carton, blending classical art with a playful, modern advertisement.',
      },
      {
        title: 'Biker Lab: The Coolest Rider in Town',
        description:
          'Forget the leash—this Labrador prefers leather jackets and high-speed jumps. Meet the ultimate biker dog!',
        videoUrl:
          'https://assets.falcocut.ai/models/seedance/labrador-retriever-motorcycle-leather-jacket.mp4',
        prompt:
          'The video starts with a cool Labrador Retriever adjusting his sunglasses with a paw, looking like a total pro. Wearing a black leather jacket, the dog hops onto a motorcycle inside a barn and revs the engine. He blasts through the barn doors and speeds onto a dirt track, his tail wagging as he handles the bike with ease. The highlight features the dog catching massive air on a jump, soaring against the blue sky. Landing smoothly, he speeds off into the distance, leaving a cloud of dust behind in this thrilling, action-packed display of canine swagger.',
      },
      {
        title: 'D-Day: The Shores of History',
        description:
          'A gritty, cinematic tribute to the bravery of soldiers landing on the stormy beaches.',
        videoUrl:
          'https://assets.falcocut.ai/models/seedance/stormy-beach-soldier-landing.mp4',
        prompt:
          "A young soldier, his face marked by sweat and terror, grips his rifle as the landing craft's ramp drops. He charges into a chaotic shoreline engulfed in black smoke and thunderous explosions. Under heavy fire, soldiers scramble through the surf, some falling while others desperately push toward the sand. The scene concludes with a harrowing shot of a soldier sprinting through the carnage, capturing the brutal intensity and raw fear of a wartime landing.",
      },
    ],
  },
  socialProof: {
    youtube: {
      title: 'YouTube Videos about Seedance 2.0',
      items: [
        { videoId: '_2qDjW3hhdM', url: 'https://www.youtube.com/watch?v=_2qDjW3hhdM' },
        { videoId: 'WW_odt7uZTs', url: 'https://www.youtube.com/watch?v=WW_odt7uZTs' },
        { videoId: 'yLQClFqzHOU', url: 'https://www.youtube.com/watch?v=yLQClFqzHOU' },
      ],
      fallbacks: [
        { videoId: '5s2l68SrJP4', url: 'https://www.youtube.com/watch?v=5s2l68SrJP4' },
        { videoId: '9hGEEbK9b3o', url: 'https://www.youtube.com/watch?v=9hGEEbK9b3o' },
      ],
    },
    reddit: {
      title: 'Reddit Discussions about Seedance 2.0',
      items: [
        {
          postUrl:
            'https://www.reddit.com/r/technology/comments/1r6aegw/bytedances_seedance_20_ai_video_generator_sparks/',
          title: "ByteDance's Seedance 2.0 AI video generator sparks...",
          subreddit: 'technology',
        },
        {
          postUrl:
            'https://www.reddit.com/r/singularity/comments/1rblgp0/just_with_a_single_prompt_and_this_result_is/',
          title: 'Just with a single prompt and this result is insane',
          author: 'u/mhu99',
          subreddit: 'singularity',
        },
        {
          postUrl:
            'https://www.reddit.com/r/ChatGPT/comments/1qya2ha/seedance_20_tester_this_ai_video_preview_actually/',
          title: 'Seedance 2.0 tester, this AI video preview actually blew me away',
          author: 'u/Traditional-Table866',
          subreddit: 'ChatGPT',
        },
      ],
      fallbacks: [
        {
          postUrl:
            'https://www.reddit.com/r/generativeAI/comments/1rbionc/this_is_terrifying_seedance_20_just_generated_a/',
          title: 'This is terrifying!! Seedance 2.0 just generated a 1-minute film',
          author: 'u/voidarix',
          subreddit: 'generativeAI',
        },
        {
          postUrl:
            'https://www.reddit.com/r/singularity/comments/1r2116u/i_gave_seedance_20_one_photo_and_it_made_me_talk/',
          title: 'I Gave Seedance 2.0 One Photo and It Made Me Talk Like a YouTuber!',
          subreddit: 'singularity',
        },
      ],
    },
    twitter: {
      title: 'Reviews of Seedance 2.0 on X',
      items: [
        { tweetId: '2020911900968767976' },
        { tweetId: '2020777634154881197' },
        { tweetId: '2022364200295645336' },
        { tweetId: '2021204908909945287' },
        { tweetId: '2020974375391101296' },
      ],
      fallbacks: [{ tweetId: '2021969985523134800' }, { tweetId: '2020492019139838353' }],
    },
  },
  exploreModels: {
    title: 'Explore More AI Models on FalcoCut',
    models: [
      { name: 'Sora 2 Pro', href: '/models/sora-2-pro' },
      { name: 'Veo 3.1', href: '/models/veo-3-1' },
      { name: 'Kling V2.6 Pro', href: '/models/kling-v2-6-pro' },
      { name: 'Kling 2.5 Turbo Pro', href: '/models/kling-2-5-turbo-pro' },
      { name: 'Seedance V1.5 Pro', href: '/models/seedance-v1-5-pro' },
      { name: 'Nano Banana 2', href: '/models/nano-banana-2' },
      { name: 'Nano Banana Pro', href: '/models/nano-banana-pro' },
      { name: 'FLUX Kontext', href: '/models/flux-kontext' },
      {
        name: 'Kling V2.6 Pro Motion Control',
        href: '/models/kling-v2-6-pro-motion-control',
      },
      {
        name: 'Kling Video O1 Std Video Edit',
        href: '/models/kling-video-o1-std-video-edit',
      },
    ],
  },
  toolkits: {
    title: 'FalcoCut AI Toolkits',
    description:
      'FalcoCut provides a full suite of AI tools for creating high-quality videos faster. From AI video generation and avatars to translation and scalable content creation, everything you need to produce engaging video content in one platform.',
    items: [
      {
        title: 'AI Video Generator',
        href: '/ai-video-generator',
        imageUrl: 'https://assets.falcocut.ai/tools/common/ai-video-generator.png',
      },
      {
        title: 'AI Avatar Generator',
        href: '/ai-avatar-generator',
        imageUrl: 'https://assets.falcocut.ai/tools/common/ai-avatar-generator.png',
      },
      {
        title: 'Product Avatar',
        href: '/ai-product-avatar',
        imageUrl: 'https://assets.falcocut.ai/tools/common/product-avatar.png',
      },
      {
        title: 'AI Video Editor',
        href: '/ai-video-editor',
        imageUrl: 'https://assets.falcocut.ai/tools/common/ai-video-editor.png',
      },
      {
        title: 'AI Video Translator',
        href: '/video-translator',
        imageUrl: 'https://assets.falcocut.ai/tools/common/ai-video-translator.png',
      },
    ],
  },
  faq: {
    title: 'Frequently Asked Questions',
    items: [
      {
        question: 'What is Seedance 2.0?',
        answer:
          '<p>Seedance 2.0 is the latest high-performance AI video foundation model developed by ByteDance to empower professional creators. It serves as a multimodal "AI Director" that can process text, images, video clips, and audio files simultaneously to generate cinematic content. By utilizing advanced physics and semantic understanding, the model produces realistic motion and complex environmental interactions in a single generation. It is widely considered a top-tier solution for brands needing high-fidelity video with consistent characters and native sound.</p>',
      },
      {
        question: 'How is Seedance 2.0 different from earlier Seedance models?',
        answer:
          '<p>Seedance 2.0 represents a significant evolution by introducing a "unified" architecture that handles audio and video in a single pass. Unlike previous versions that focused primarily on visual motion, this model ensures that every sound effect and lip movement is perfectly synchronized with the action. The 2.0 update also introduces the @Reference system, which allows users to upload up to twelve different assets for pinpoint creative control. Furthermore, it offers vastly improved physical stability and 2K resolution support, making it suitable for professional advertising and filmmaking.</p>',
      },
      {
        question: 'What inputs does Seedance 2.0 support?',
        answer:
          '<p>Seedance 2.0 supports a comprehensive "quad-modal" input system including text prompts, images, video references, and audio files. You can upload up to nine images to define characters and three video clips to guide specific camera or body movements. Additionally, the model accepts three audio files to influence the rhythm, background music, or dialogue of the generated scene. By tagging these assets with the "@" symbol in your prompt, you can direct the AI with surgical precision.</p>',
      },
      {
        question: 'Can Seedance 2.0 generate audio and video together?',
        answer:
          '<p>Yes, Seedance 2.0 is specifically designed to generate high-quality audio and video simultaneously within the same diffusion process. This native synchronization ensures that environmental sound effects and background music perfectly match the visual energy of every frame. Because the audio is generated alongside the video, the model can produce realistic lip-syncing for characters in multiple different languages. This integrated approach eliminates the need for complex post-production work and delivers a final product that is ready for immediate use.</p>',
      },
      {
        question: 'Is Seedance 2.0 free to use?',
        answer:
          '<p>No, Seedance 2.0 is generally a premium model that requires credits or a paid subscription for full access on professional platforms. While some regional versions offer daily free credits or trial periods, high-resolution exports and unlimited generations typically require a membership. On FalcoCut, you can often start with a basic allowance, but advanced "Director Mode" features are reserved for professional-tier users. Investing in a plan ensures you have the rendering speed and output quality necessary for serious commercial or creative projects.</p>',
      },
      {
        question: 'How do I use the Seedance 2.0 text-to-video generator on FalcoCut?',
        answer:
          '<ol><li><strong>Step 1:</strong> Log into your FalcoCut account and select the Seedance 2.0 model from the main AI video dashboard.</li><li><strong>Step 2:</strong> Enter a detailed description of your scene in the prompt box and use the upload tool to add any reference images or videos. Be sure to use the "@" tag system to link your files, such as "@Image1" for your main character\'s appearance.</li><li><strong>Step 3:</strong> Choose your desired aspect ratio and click "Generate" to create your high-definition, audio-synced video in just a few moments.</li></ol>',
      },
      {
        question: 'How can I access Seedance 2.0?',
        answer:
          "<p>You can access Seedance 2.0 through specialized AI creative suites like FalcoCut, which provide a streamlined interface for global users. It is also available via ByteDance's official platforms such as Jimeng (Dreamina) and the Doubao ecosystem in specific regions. Many creators use API-integrated tools that offer the model's full \"Director Mode\" capabilities for professional advertising and social media workflows. By using a cloud-based platform, you can harness the model's immense processing power without needing a high-end computer at home.</p>",
      },
      {
        question: 'Can I create multi-shot or multi-scene videos with Seedance 2.0?',
        answer:
          '<p>Yes, Seedance 2.0 is uniquely engineered to handle multi-shot storytelling by breaking a single prompt into a sequence of cinematic cuts. The model maintains perfect visual continuity for characters and environments even as the camera switches between wide, medium, and close-up angles. This narrative logic allows you to generate a full 15-second story that feels like a professionally edited film rather than a single clip. It is the ideal tool for creators who need to tell a complex brand story with high energy and professional pacing.</p>',
      },
      {
        question: 'Is Seedance 2.0 better than other video models like Sora 2, Veo 3.1?',
        answer:
          '<p>While "better" is subjective, Seedance 2.0 is often preferred for its superior multimodal control and the precision of its @Reference system. While Sora 2 and Veo 3.1 offer incredible realism, Seedance 2.0 provides creators with the specific tools needed to lock in character identity and motion styles. The ability to generate native audio alongside 2K video gives it a distinct advantage for users who want an all-in-one production solution. It is currently the leading choice for agencies and marketers who require consistent, directed results rather than random AI outputs.</p>',
      },
    ],
  },
  cta: {
    title: 'Try Seedance 2.0 Now',
    description: '',
    buttonText: 'Try Seedance 2.0 Now',
    imageUrl: 'https://assets.falcocut.ai/models/sora/try-sora-2-pro-on-falcocut.png',
  },
  bottomCta: {
    title: 'Start Creating Videos with Seedance 2.0',
    description: '',
    buttonText: 'Try Seedance 2.0 Now',
  },
  seo: {
    title: 'Seedance 2.0 AI Video Generator – Next‑Gen Multimodal AI Video Creation',
    description:
      "Create high‑quality videos with Seedance 2.0 on FalcoCut, powered by ByteDance's next‑gen multimodal AI. Turn text, images, video, and audio references into fast high‑resolution animations with precise control over style, motion, rhythm, and narrative.",
    keywords:
      'Seedance 2.0, AI video generator, text to video, ByteDance, AI video creation, FalcoCut',
    publishedTime: '2026-03-16',
    modifiedTime: '2026-03-16',
  },
};

export default config;
